/**
 * @reflecto/client-core — clipHistory/index.ts
 *
 * Clip history — persisted clipboard timeline for THIS device.
 *
 * One implementation, both PC hosts (spec C2): pure logic over a
 * `StorageAdapter`, exactly like `createConversations`. The extension passes
 * its `browser.storage.local` wrapper, web its `idb-keyval` impl; they differ
 * only by `budgetBytes` (C3).
 *
 * Sync mirror + async persistence: hosts render from `snapshot()` without
 * awaiting, while writes go to disk in the background. `hydrate()` does the
 * one-time load.
 *
 * SECURITY: entries hold decrypted plaintext (C1). Never log an entry or any
 * part of its `content` — failures here log nothing at all.
 */

import type { StorageAdapter } from "../storage/adapter";

/** Per-surface byte budgets (C3) — constructor options, not hardcoded in the factory. */
export const CLIP_HISTORY_BUDGET_WEB = 16 * 1024 * 1024;
export const CLIP_HISTORY_BUDGET_EXTENSION = 4 * 1024 * 1024;

export interface ClipHistoryEntry {
  id: string;
  direction: "sent" | "received";
  content: string;
  /** Display only — NEVER hash this (C4: renaming a device must not change derived identity). */
  peerLabel: string;
  /** Stable device identity. Undefined when unknown (e.g. broadcast-to-all). */
  peerDeviceId?: string;
  peerSurface?: string;
  /**
   * Source of this entry — "device" (a paired device, E2E) or "cloud" (the
   * public `/v1/send` API / remote MCP, server-as-sender). Same argument as
   * `peerDeviceId` above (C4): carried from day one because adding a field
   * after ship means migrating persisted plaintext user history, which this
   * store's own no-expiry rule (C5) says is not disposable. Unlike
   * `peerDeviceId`, this one already has a v1 renderer — `ClipCard` shows a
   * wary "from your API token" chip when `provenance === "cloud"` (PRD
   * §7.9) — so hosts should populate it wherever the origin is known
   * instead of leaving it undefined by default. Optional so existing call
   * sites and the extension host (not yet wired) stay compiling.
   */
  provenance?: "device" | "cloud";
  sensitive?: boolean;
  timestamp: number;
}

export interface ClipHistoryApi {
  /** One-time load into the sync mirror. Idempotent; concurrent callers share one load. */
  hydrate(): Promise<void>;
  /** Sync, newest-first read of the in-memory mirror. Empty until `hydrate()` (or another op) has run. */
  snapshot(): ClipHistoryEntry[];
  /** Authoritative read — hydrates first if needed, then returns the mirror. */
  list(): Promise<ClipHistoryEntry[]>;
  /** Dedups by `id`, unshifts (newest-first), evicts to budget, persists. */
  add(entry: ClipHistoryEntry): Promise<void>;
  /** Removes one entry by id. Unknown id is a data no-op but still notifies (FIX 3 — see remove()'s body comment). */
  remove(id: string): Promise<void>;
  /** Empties both the mirror and storage. */
  clear(): Promise<void>;
  /**
   * Fires on add/remove/clear, and once on a hydrate() that actually
   * populates the mirror (not on an idempotent already-hydrated re-entry,
   * and not when a concurrent clear() superseded the in-flight read).
   * Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void;
}

/**
 * Create a clip-history module bound to a specific `StorageAdapter` and
 * byte budget. Each call returns its own private in-memory mirror — host
 * applications typically call this once at startup and re-export the
 * operations.
 */
export function createClipHistory(
  storage: StorageAdapter,
  opts: { budgetBytes: number }
): ClipHistoryApi {
  const { budgetBytes } = opts;

  // Sync in-memory mirror, newest-first. Hosts render from this without
  // awaiting; persistence happens in the background on every mutation.
  let mirror: ClipHistoryEntry[] = [];
  // Memoized snapshot() result (FIX 2). `useSyncExternalStore`-style
  // consumers require getSnapshot() to return a referentially stable value
  // when nothing changed, or React re-renders forever. Invalidated (set to
  // null) by setMirror() below, which is the ONLY place `mirror` is ever
  // reassigned — so the cache and the mirror can never drift apart. The
  // cached array is still a fresh `[...mirror]` copy, never the live
  // `mirror` reference itself: a caller mutating what snapshot() returns
  // can corrupt the cache (visible on the next unchanged-mirror snapshot()
  // call) but can never reach `mirror`, which is what add()/remove()/evict/
  // persist actually read from.
  let cachedSnapshot: ClipHistoryEntry[] | null = null;
  let hydrated = false;
  // De-dupe concurrent hydrate() callers — same reason as
  // createConversations' ensureIndexLoaded: two callers arriving before the
  // first storage round-trip resolves would otherwise both re-read.
  let hydrating: Promise<void> | null = null;
  // Write-version counter. Bumped synchronously by every operation that
  // mutates the mirror and persists it — add(), a changed remove(), and
  // clear() — each capturing the POST-bump value as "the version this
  // write's data reflects" before calling persist() (FIX 4, see persist()
  // below). Two independent consumers read it:
  //
  //   - hydrate() captures the version it is reading FOR and discards its
  //     result if this moved on before the read resolved — i.e. a clear()
  //     ran while the load was in flight. In practice only clear() can
  //     move it while a read is genuinely in flight: add()/remove() always
  //     `await hydrate()` before touching `generation`, so they can never
  //     reach their own bump until any in-flight read has already
  //     resolved. Without this, clear()'s direct mirror/storage wipe could
  //     be clobbered by a stale in-flight hydrate() completing afterward
  //     with pre-clear data, resurrecting wiped plaintext (IMPORTANT-1).
  //     `createConversations.addConversationMessageIfNew` avoids the
  //     equivalent race by awaiting the index load before any mutation;
  //     `clear()` here can't do that (it must win unconditionally, even
  //     over a load that's already running), so it invalidates instead.
  //   - persist() re-checks this after every storage.set() attempt to
  //     detect whether a NEWER write — by dispatch order, not by whichever
  //     storage.set() call the adapter happens to settle first — has since
  //     been authored, and if so self-heals by re-writing the current
  //     mirror under the current version (FIX 4).
  let generation = 0;

  const listeners = new Set<() => void>();

  function notify(): void {
    for (const listener of listeners) listener();
  }

  // The ONLY place `mirror` is reassigned (FIX 2). Invalidates the memoized
  // snapshot() cache in lockstep so it can never serve stale data after a
  // real mirror change, and never needs a second, separately-maintained
  // invalidation call at each mutation site.
  function setMirror(next: ClipHistoryEntry[]): void {
    mirror = next;
    cachedSnapshot = null;
  }

  // Deliberately `JSON.stringify(...).length` (UTF-16 code units), not exact
  // UTF-8 byte length. This is monotonic in content size, which is all
  // eviction needs, and avoids a TextEncoder round-trip on every write.
  function byteSize(list: ClipHistoryEntry[]): number {
    return JSON.stringify(list).length;
  }

  function evict(list: ClipHistoryEntry[]): void {
    // `length > 1` guard: never evict the last remaining entry, even if it
    // alone exceeds the budget — a single oversized clip is kept rather
    // than silently swallowed (spec: "never silently swallow the user's
    // clip"). The next `add` will evict it once something newer exists.
    while (list.length > 1 && byteSize(list) > budgetBytes) {
      list.pop();
    }
  }

  async function persist(list: ClipHistoryEntry[], gen: number): Promise<void> {
    // Round-1's generation guard only protected hydrate()'s READ. A write
    // authored by add()/remove() before a clear() can still land in
    // storage AFTER clear()'s own write, if the adapter happens to settle
    // the two storage.set() calls out of call order — resurrecting cleared
    // plaintext through the WRITE path instead of the read path (same C5
    // violation, different door). A single pre-write check can't catch
    // this: by the time add()'s write is dispatched, clear() may not have
    // run yet, so the check passes — the staleness only becomes knowable
    // *after* the write settles. Loop instead: after every attempt,
    // re-check whether `generation` moved on while it was in flight; if
    // so, this write may have just clobbered a fresher one, so re-assert
    // the CURRENT mirror under the CURRENT generation and try again. A
    // writer authored before a clear() can therefore never have the last
    // word once that clear() has run.
    //
    // FIX 4: `generation` is no longer bumped by clear() alone — add() and
    // a changed remove() bump it too, so two concurrent add()/add() or
    // add()/remove() calls get distinct versions instead of silently
    // sharing one (which made this exact loop a no-op between them: same
    // gen in, same gen out, so whichever write settled last just won,
    // regardless of which one had the more complete data). The retry loop
    // itself needs no other change: whichever of two concurrent writes
    // settles LAST always notices via this check that a newer version
    // exists and re-asserts the CURRENT mirror — which, by construction,
    // is the union of both calls' effects, since add()/remove() mutate
    // `mirror` synchronously in dispatch order before ever yielding to
    // persist(). So two concurrent writers can never leave storage holding
    // only the less-complete of the two snapshots, regardless of which
    // storage.set() call the adapter happens to settle first.
    let toWrite = list;
    let writeGen = gen;
    for (;;) {
      if (writeGen !== generation) return; // superseded before this attempt could start
      try {
        await storage.set({ clipHistory: toWrite });
      } catch {
        // Swallow: a full disk / quota error must not lose the clip from the
        // running session (the mirror already reflects it) or crash the
        // caller. SECURITY: never log the caught error — it could carry
        // clip content via structured logging of the failed write.
        return;
      }
      if (writeGen === generation) return; // still authoritative when it landed — done
      toWrite = mirror;
      writeGen = generation;
    }
  }

  async function readStoredHistory(): Promise<ClipHistoryEntry[] | undefined> {
    try {
      const { clipHistory } = await storage.get("clipHistory");
      return clipHistory;
    } catch {
      // Degrade to an empty mirror instead of propagating. This
      // deliberately diverges from createConversations' ensureIndexLoaded,
      // which lets a read failure reject its caller — that index has a
      // correctness invariant (msgId lookups) worth failing loudly for.
      // clipHistory has no such invariant; rejecting here would make every
      // add()/remove()/list() call fail on a single bad read, which is
      // worse than starting from an empty history. SECURITY: never log
      // the caught error — it must not be inspected or serialized.
      return undefined;
    }
  }

  async function hydrate(): Promise<void> {
    if (hydrated) return;
    if (hydrating) return hydrating;
    // Capture the generation this load is reading FOR (see the `generation`
    // declaration above). Checked against the live value once the read
    // resolves.
    const gen = generation;
    hydrating = (async () => {
      try {
        const clipHistory = await readStoredHistory();
        if (gen === generation) {
          setMirror(clipHistory ?? []);
          hydrated = true;
          // FIX 3: notify only on a real, non-superseded load — never on
          // the `if (hydrated) return` no-op re-entry above (that path
          // never reaches this closure at all), and never when the
          // `gen !== generation` branch below discarded a stale read.
          // Without this, a subscriber registered before the first
          // hydrate() resolves — the common case for a host wiring
          // useSyncExternalStore at mount while hydrate() runs elsewhere —
          // never learns the initial history arrived.
          notify();
        }
        // else: a clear() invalidated this load while it was in flight —
        // its result is stale (pre-clear), discard it. clear() already
        // notified for the (empty) state it's authoritative for; notifying
        // again here with pre-clear data would be wrong twice over.
      } finally {
        hydrating = null;
      }
    })();
    return hydrating;
  }

  function snapshot(): ClipHistoryEntry[] {
    // Memoized (FIX 2): return the SAME array reference across calls until
    // `mirror` actually changes, so `useSyncExternalStore`-style consumers
    // don't re-render forever. Still a shallow copy of `mirror`, never the
    // live reference itself (IMPORTANT-2) — add()/remove()/evict/persist
    // read from `mirror`, not from this cache, so a caller mutating what
    // snapshot() returns can never corrupt them.
    if (cachedSnapshot === null) {
      cachedSnapshot = [...mirror];
    }
    return cachedSnapshot;
  }

  async function list(): Promise<ClipHistoryEntry[]> {
    await hydrate();
    return [...mirror];
  }

  async function add(entry: ClipHistoryEntry): Promise<void> {
    await hydrate();
    const next = mirror.filter((e) => e.id !== entry.id);
    next.unshift(entry);
    evict(next);
    // Bumped (not just read) AFTER hydrate() resolves and `next` is built
    // from the current `mirror` — i.e. "the version this write's DATA
    // reflects", not just when the call happened. Bumping rather than
    // reading gives this specific write its own distinct version, which is
    // what lets persist()'s retry loop tell two concurrent add() writes
    // apart (FIX 4 — see the `generation` declaration above). Doing the
    // bump here, after the hydrate() wait, keeps the pre-existing
    // clear()-straddling guarantee too: a legitimate add() that straddles
    // a concurrent clear() during the hydrate() wait is already building
    // `next` from the post-clear mirror, so its bump lands strictly after
    // clear()'s own — never mistaken for a stale writer.
    generation++;
    const gen = generation;
    setMirror(next);
    notify();
    await persist(mirror, gen);
  }

  async function remove(id: string): Promise<void> {
    await hydrate();
    const next = mirror.filter((e) => e.id !== id);
    const changed = next.length !== mirror.length;
    if (changed) {
      // Bump — see add()'s comment on why bumping (not just reading) here,
      // after hydrate() resolves, both preserves the clear()-straddling
      // guarantee and gives this write its own distinct version for
      // persist()'s FIX 4 retry loop. Only bumped when `changed`: an
      // unknown-id no-op never calls persist(), so it must not consume a
      // version another concurrent write could need for its own staleness
      // check.
      generation++;
      setMirror(next);
    }
    const gen = generation; // captured after the conditional bump above
    // FIX 3: always notify, including the unknown-id no-op. Chosen over
    // "never notify on a no-op" because that alternative requires every
    // caller to separately reason about whether the memoized snapshot()
    // cache could be stale after a no-op — "always notify" has no such
    // edge case to get wrong, and is cheap: when `changed` is false the
    // mirror (and therefore the cached snapshot reference) is untouched,
    // so a useSyncExternalStore-style consumer's getSnapshot() still
    // returns the same reference and bails out of re-rendering.
    notify();
    if (changed) {
      await persist(mirror, gen);
    }
  }

  async function clear(): Promise<void> {
    // Invalidate any hydrate() already in flight BEFORE mutating state, so
    // its completion handler's generation check (in hydrate() above)
    // discards a pre-clear read instead of resurrecting it (IMPORTANT-1).
    generation++;
    const gen = generation;
    setMirror([]);
    hydrated = true;
    notify();
    await persist(mirror, gen);
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    hydrate,
    snapshot,
    list,
    add,
    remove,
    clear,
    subscribe,
  };
}
